import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, MinusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Formulaire de création de demande Business Unit.
 * Accessible uniquement au rôle business_unit. Le motif est fixé à
 * 'business_unit' → la demande est routée directement vers la directrice.
 */
const CreateBusinessUnitRequest = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');

  // Réserver l'accès au rôle business_unit
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'business_unit') {
      toast.error('Vous n\'êtes pas autorisé à accéder à cette page');
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  // Charger les produits au montage du composant
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get('/api/products');
        setProducts(response.data.data || []);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Erreur lors du chargement des produits');
      }
    };

    fetchProducts();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: currentUser?.name || '',
      email: currentUser?.email || '',
      phone: '',
      organization: '',
      selectedProducts: [{ productId: '', quantity: 1 }],
      additionalInfo: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Le nom est requis'),
      email: Yup.string().email('Email invalide').required('L\'email est requis'),
      phone: Yup.string().required('Le numéro de téléphone est requis'),
      organization: Yup.string().required('L\'organisation est requise'),
      selectedProducts: Yup.array()
        .of(
          Yup.object().shape({
            productId: Yup.string().required('Produit requis'),
            quantity: Yup.number()
              .required('Quantité requise')
              .positive('La quantité doit être positive')
              .integer('La quantité doit être un nombre entier'),
          })
        )
        .min(1, 'Au moins un produit doit être sélectionné'),
      additionalInfo: Yup.string(),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);

        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('email', values.email);
        formData.append('phone', values.phone);
        formData.append('organization', values.organization);
        formData.append('requestType', 'business_unit'); // Type fixe pour ce formulaire
        formData.append('reason', 'Business Unit');
        formData.append('selectedProducts', JSON.stringify(values.selectedProducts));
        formData.append('additionalInfo', values.additionalInfo);

        if (selectedFile) {
          formData.append('attachment', selectedFile);
        }

        const response = await api.post('/api/requests', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        toast.success('Demande Business Unit créée avec succès');
        navigate(`/requests/${response.data.id}`);
      } catch (error) {
        console.error('Error submitting request:', error);
        toast.error(error.response?.data?.error || 'Erreur lors de la création de la demande');
      } finally {
        setLoading(false);
      }
    },
  });

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFileError('');

    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setFileError('Le fichier est trop volumineux. Taille maximale: 5MB');
        return;
      }

      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setFileError('Type de fichier non supporté. Formats acceptés: PDF, JPEG, PNG');
        return;
      }

      setSelectedFile(file);
    }
  };

  const addProductRow = () => {
    formik.setFieldValue('selectedProducts', [
      ...formik.values.selectedProducts,
      { productId: '', quantity: 1 },
    ]);
  };

  const removeProductRow = (index) => {
    if (formik.values.selectedProducts.length > 1) {
      const updatedProducts = [...formik.values.selectedProducts];
      updatedProducts.splice(index, 1);
      formik.setFieldValue('selectedProducts', updatedProducts);
    }
  };

  const handleQuantityChange = (index, value) => {
    const newValue = Math.max(1, parseInt(value) || 0);
    const updatedProducts = [...formik.values.selectedProducts];
    updatedProducts[index].quantity = newValue;
    formik.setFieldValue('selectedProducts', updatedProducts);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Créer une demande Business Unit</h1>
        <button
          onClick={() => navigate('/requests')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={formik.handleSubmit} className="space-y-6">
          {/* Informations du demandeur */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Informations du demandeur</h3>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Nom complet
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className={`mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${
                    formik.touched.name && formik.errors.name ? 'border-red-500' : ''
                  }`}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.name && formik.errors.name && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Adresse email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${
                    formik.touched.email && formik.errors.email ? 'border-red-500' : ''
                  }`}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Numéro de téléphone
                </label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  className={`mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${
                    formik.touched.phone && formik.errors.phone ? 'border-red-500' : ''
                  }`}
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.phone && formik.errors.phone && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.phone}</p>
                )}
              </div>

              <div>
                <label htmlFor="organization" className="block text-sm font-medium text-gray-700">
                  Organisation
                </label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  className={`mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${
                    formik.touched.organization && formik.errors.organization ? 'border-red-500' : ''
                  }`}
                  value={formik.values.organization}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.organization && formik.errors.organization && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.organization}</p>
                )}
              </div>
            </div>
          </div>

          {/* Produits demandés */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Produits demandés</h3>

            {formik.values.selectedProducts.map((product, index) => (
              <div key={index} className="flex items-center space-x-2 mb-2">
                <div className="flex-grow">
                  <select
                    name={`selectedProducts[${index}].productId`}
                    value={product.productId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      formik.touched.selectedProducts?.[index]?.productId &&
                      formik.errors.selectedProducts?.[index]?.productId
                        ? 'border-red-500'
                        : ''
                    }`}
                  >
                    <option value="">Sélectionnez un produit</option>
                    {Array.isArray(products) && products.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  {formik.touched.selectedProducts?.[index]?.productId &&
                   formik.errors.selectedProducts?.[index]?.productId && (
                    <p className="mt-2 text-sm text-red-600">
                      {formik.errors.selectedProducts[index].productId}
                    </p>
                  )}
                </div>

                <div className="w-32">
                  <div className="flex items-center">
                    <button
                      type="button"
                      className="bg-gray-200 p-1 rounded-l-md"
                      onClick={() => handleQuantityChange(index, product.quantity - 1)}
                    >
                      <MinusIcon className="h-5 w-5 text-gray-600" />
                    </button>
                    <input
                      type="number"
                      name={`selectedProducts[${index}].quantity`}
                      value={product.quantity}
                      onChange={(e) => handleQuantityChange(index, e.target.value)}
                      onBlur={formik.handleBlur}
                      className={`form-input rounded-none border-l-0 border-r-0 text-center ${
                        formik.touched.selectedProducts?.[index]?.quantity &&
                        formik.errors.selectedProducts?.[index]?.quantity
                          ? 'border-red-500'
                          : ''
                      }`}
                      min="1"
                    />
                    <button
                      type="button"
                      className="bg-gray-200 p-1 rounded-r-md"
                      onClick={() => handleQuantityChange(index, product.quantity + 1)}
                    >
                      <PlusIcon className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>
                  {formik.touched.selectedProducts?.[index]?.quantity &&
                   formik.errors.selectedProducts?.[index]?.quantity && (
                    <p className="mt-2 text-sm text-red-600">
                      {formik.errors.selectedProducts[index].quantity}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => removeProductRow(index)}
                  className="p-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                  disabled={formik.values.selectedProducts.length === 1}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={addProductRow}
              className="mt-2 flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Ajouter un produit
            </button>

            {formik.touched.selectedProducts &&
             typeof formik.errors.selectedProducts === 'string' && (
              <p className="mt-2 text-sm text-red-600">{formik.errors.selectedProducts}</p>
            )}
          </div>

          {/* Informations supplémentaires */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Informations supplémentaires</h3>

            <div className="mb-6">
              <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700">
                Détails de la demande (optionnel)
              </label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                rows={3}
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                value={formik.values.additionalInfo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Précisez le contexte de la demande Business Unit..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Pièce jointe (optionnel)</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>Télécharger un fichier</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                    </label>
                    <p className="pl-1">ou glisser-déposer</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    PDF, PNG, JPG jusqu'à 5MB
                  </p>
                  {selectedFile && (
                    <p className="text-sm text-green-600">
                      Fichier sélectionné: {selectedFile.name}
                    </p>
                  )}
                  {fileError && (
                    <p className="text-sm text-red-600">{fileError}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {loading ? 'Création en cours...' : 'Créer la demande'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBusinessUnitRequest;
